import type { ChangeEventHandler, FunctionComponent } from 'react';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { Button } from '@trello/nachos/button';
import { Dialog, useDialog } from '@trello/nachos/dialog';
import { KeyIcon } from '@trello/nachos/icons/key';
import { Textfield } from '@trello/nachos/textfield';
import type { AppManagementTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { token } from '@trello/theme';

import { useCreatePluginApplicationMutation } from '../queries/CreatePluginApplicationMutation.generated';
import { usePluginApplicationQuery } from '../queries/PluginApplicationQuery.generated';
import { GenerateApiKeyModal } from './GenerateApiKeyModal';

import styles from './PowerUpGenerateApiKeyPage.less';

export const PowerUpGenerateApiKeyPage: FunctionComponent<{
  idPlugin: string;
  onGenerate: () => void;
}> = ({ onGenerate, idPlugin }) => {
  const intl = useIntl();
  const [apiKey, setApiKey] = useState<string>('');
  const [isInvalidApiKey, setIsInvalidApiKey] = useState(false);
  const [unauthorizedApiKey, setUnauthorizedApiKey] = useState(false);
  const [createPluginApplicationMutation] =
    useCreatePluginApplicationMutation();
  const { data: pluginApplicationData } = usePluginApplicationQuery({
    variables: {
      pluginId: idPlugin,
      getMemberApplication: true,
    },
    waitOn: ['None'],
  });

  const generateApiKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (existingApiKey: any = null) =>
      async () => {
        const taskName = 'edit-plugin/generateApiKey';
        const source = 'powerUpEditScreen';
        if (/^[a-f0-9]{32}$/.test(existingApiKey) || existingApiKey === null) {
          const traceId = Analytics.startTask({
            taskName,
            source,
          });
          try {
            await createPluginApplicationMutation({
              variables: {
                pluginId: idPlugin,
                key: existingApiKey,
              },
            });
            onGenerate();
            Analytics.taskSucceeded({
              taskName,
              traceId,
              source,
            });
          } catch (error) {
            setUnauthorizedApiKey(true);
            setIsInvalidApiKey(false);
            Analytics.taskFailed({
              taskName,
              traceId,
              source,
              error,
            });
            sendErrorEvent(error, {
              tags: { ownershipArea: 'trello-web-eng' },
            });
          }
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const value = e.target.value;
      setApiKey(value);
      if (/^[a-f0-9]{32}$/.test(value)) {
        setIsInvalidApiKey(false);
      } else {
        setIsInvalidApiKey(true);
        setUnauthorizedApiKey(false);
      }
    },
    [setApiKey, setIsInvalidApiKey],
  );

  const isOldApiKeyConvertedToPlugin = !pluginApplicationData;

  const { dialogProps, show, hide, toggle } = useDialog();

  return (
    <div
      data-testid={getTestId<AppManagementTestIds>(
        'power-up-generate-api-key-page',
      )}
    >
      <div className={styles.header}>
        <h1>
          <FormattedMessage
            id="templates.app_management.api-key"
            defaultMessage="API key"
            description="Title for the API key page"
          />
        </h1>
      </div>

      <div className={styles.generate}>
        <div className={styles.generateInfo}>
          <div className={styles.iconKey}>
            <KeyIcon
              size="large"
              color={token('color.icon.accent.purple', '#8270DB')}
            />
          </div>
          <div>
            <h3>
              <FormattedMessage
                id="templates.app_management.better-way-to-manage-api-keys"
                defaultMessage="A better way to manage API keys"
                description="Heading for the API key management section"
              />
            </h3>
            <p>
              <FormattedMessage
                id="templates.app_management.api-key-introduction"
                defaultMessage="Each Power-Up can now have a unique API key and be managed here by any collaborators."
                description="Introduction text for API key management"
              />{' '}
              {isOldApiKeyConvertedToPlugin ? (
                <FormattedMessage
                  id="templates.app_management.to-get-started-generate-a-new-api-key"
                  defaultMessage="To get started, generate a new API key."
                  description="Instruction to generate a new API key"
                />
              ) : (
                <FormattedMessage
                  id="templates.app_management.use-existing-or-generate-a-new-api-key"
                  defaultMessage="To get started, you can use <a>your existing API key</a> or generate a new API key."
                  description="Option to use existing or generate new API key"
                  values={{
                    a: (chunks) => <a href="/app-key">{chunks}</a>,
                  }}
                />
              )}
            </p>
          </div>
        </div>
        <div className={styles.generate}>
          {isOldApiKeyConvertedToPlugin ? null : (
            <div>
              <Textfield
                label={intl.formatMessage({
                  id: 'templates.app_management.api-key',
                  defaultMessage: 'API key',
                  description: 'Label for API key input field',
                })}
                placeholder={intl.formatMessage({
                  id: 'templates.app_management.enter-existing-api-key',
                  defaultMessage: 'Enter existing API key',
                  description: 'Placeholder text for API key input field',
                })}
                className={styles.textfield}
                iconAfter={
                  <Button
                    className={styles.submitBtn}
                    size="wide"
                    children={
                      <FormattedMessage
                        id="templates.app_management.use-existing-api-key"
                        defaultMessage="Use existing API key"
                        description="Button text to use existing API key"
                      />
                    }
                    appearance="primary"
                    onClick={generateApiKey(apiKey)}
                  />
                }
                onChange={onChange}
                isInvalid={isInvalidApiKey}
              />
              {isInvalidApiKey && (
                <span
                  className={styles.isInvalidApiKey}
                  data-testid={getTestId<AppManagementTestIds>(
                    'power-up-existing-api-key-invalid',
                  )}
                >
                  <FormattedMessage
                    id="templates.app_management.not-a-valid-api-key"
                    defaultMessage="Invalid API key."
                    description="Error message for invalid API key"
                  />
                </span>
              )}
              {unauthorizedApiKey && (
                <span
                  className={styles.isInvalidApiKey}
                  data-testid={getTestId<AppManagementTestIds>(
                    'power-up-existing-api-key-invalid',
                  )}
                >
                  <FormattedMessage
                    id="templates.app_management.unauthorized-api-key"
                    defaultMessage="Invalid API key. Follow the link above to get your existing API key."
                    description="Error message for unauthorized API key"
                  />
                </span>
              )}
              <div className={styles.optionSeparator}>
                <FormattedMessage
                  id="templates.app_management.or"
                  defaultMessage="or"
                  description="Text separating two options"
                />
              </div>
            </div>
          )}
          <Button
            children={
              <FormattedMessage
                id="templates.app_management.generate-a-new-api-key"
                defaultMessage="Generate a new API key"
                description="Button text to generate new API key"
              />
            }
            className={styles.generateBtn}
            appearance={'default'}
            onClick={show}
          />
        </div>
      </div>

      <Dialog
        {...dialogProps}
        show={show}
        hide={hide}
        toggle={toggle}
        size="large"
        alignment={'center'}
        className={styles.dialog}
      >
        <GenerateApiKeyModal generateApiKey={generateApiKey} hide={hide} />
      </Dialog>
    </div>
  );
};
